import { observer } from 'mobx-react-lite'
import React from 'react'

import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'

import { Text } from '@mxenabled/mxui'
import { ChevronRight, Visibility, VisibilityOff } from '@mxenabled/mx-icons'

import { Dialog, useGlobalCopyStore, useSettingsStore } from '../../../../common'
import {
  HAS_LETTER,
  HAS_NUMBER,
  HIDE_CONFIRM_PASSWORD,
  HIDE_NEW_PASSWORD,
  MIN_PASSWORD_LENGTH,
  PASSWORD_REQUIREMENTS,
  SHOW_CONFIRM_PASSWORD,
  SHOW_NEW_PASSWORD,
} from '../../../constants/Settings'

const ResetPasswordAction = observer(() => {
  const { common: commonCopy, settings: settingsCopy } = useGlobalCopyStore()
  const { updatePassword } = useSettingsStore()

  const [isOpen, setIsOpen] = React.useState(false)
  const [newPassword, setNewPassword] = React.useState('')
  const [confirmPassword, setConfirmPassword] = React.useState('')
  const [showNewPassword, setShowNewPassword] = React.useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false)
  const isValid =
    newPassword.length >= MIN_PASSWORD_LENGTH &&
    HAS_LETTER.test(newPassword) &&
    HAS_NUMBER.test(newPassword) &&
    newPassword === confirmPassword

  const handleClose = (): void => {
    setIsOpen(false)
    setNewPassword('')
    setConfirmPassword('')
    setShowNewPassword(false)
    setShowConfirmPassword(false)
  }

  const handleOpen = (): void => {
    setIsOpen(true)
  }

  const handleSave = () => {
    updatePassword({ new_password: newPassword, new_password_again: confirmPassword })
  }

  return (
    <Stack>
      <Button onClick={handleOpen}>
        <Text variant="body2">{settingsCopy.reset_password}</Text>
        <ChevronRight />
      </Button>
      <Dialog
        copy={{ title: settingsCopy.reset_password }}
        disablePrimaryButton={!isValid}
        isOpen={isOpen}
        onClose={handleClose}
        onPrimaryAction={handleSave}
        onSecondaryAction={handleClose}
        primaryText={settingsCopy.save_password}
        secondaryText={commonCopy.cancel_button}
        sx={{
          '& .MuiToolbar-root:last-child': {
            padding: 24,
            justifyContent: 'flex-end',
          },
        }}
      >
        <Stack spacing={16} sx={{ px: 40, textAlign: 'center', width: '100%' }}>
          <Text variant="body1">{PASSWORD_REQUIREMENTS}</Text>
          <TextField
            label={settingsCopy.new_password}
            onChange={(e) => setNewPassword(e.target.value)}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showNewPassword ? HIDE_NEW_PASSWORD : SHOW_NEW_PASSWORD}
                      edge="end"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
          />
          <TextField
            label={settingsCopy.confirm_new_password}
            onChange={(e) => setConfirmPassword(e.target.value)}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        showConfirmPassword ? HIDE_CONFIRM_PASSWORD : SHOW_CONFIRM_PASSWORD
                      }
                      edge="end"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
          />
        </Stack>
      </Dialog>
    </Stack>
  )
})

export default ResetPasswordAction
